/**
 * The database grid's edit-cell key — the ONE place its format is known.
 *
 * The open cell editors, their errors and their in-flight saves are all keyed
 * `${rowid}:${column}`. The rowid is NOT colon-free: on SQLite it is a number,
 * but on Firebird `__rowid` is the JSON-encoded primary-key tuple, so a
 * `TIMESTAMP` or `TIME` key carries colons of its own (`["2020-01-01
 * 10:00:00"]:NOTES`). Splitting on the first colon then yields a garbage
 * rowid and column — #898: every open editor on such a table was pruned as
 * though its column had been dropped, and Save All sent a truncated rowid.
 *
 * So a key is never split blind. Asked "is this key one of this row's?", the
 * answer is a prefix test against a rowid we already hold: a number contains
 * no colon and a JSON array closes with its own `]`, so `${rowid}:` cannot be
 * the prefix of another row's key. Asked "which row is this?", the answer
 * strips the cell's own column (every edit cell stores it) off the END.
 *
 * The format itself is unchanged — keys restored from a saved view state
 * still match.
 */

export type GridRowId = number | string

export function editCellKey(rowid: GridRowId, column: string): string {
  return `${rowid}:${column}`
}

/** Is `key` a cell of `rowid`? */
export function isEditKeyOfRow(key: string, rowid: GridRowId): boolean {
  return key.startsWith(`${rowid}:`)
}

/**
 * The rowid of `key`, given the column its cell is for. A numeric rowid comes
 * back as a number (SQLite's `__rowid`), anything else as the string the grid
 * was given (Firebird's JSON tuple). `null` when the key is not that column's.
 */
export function editKeyRowid(key: string, column: string): GridRowId | null {
  const suffix = `:${column}`
  if (!key.endsWith(suffix) || key.length === suffix.length) return null
  const raw = key.slice(0, key.length - suffix.length)
  const num = Number(raw)
  return Number.isNaN(num) ? raw : num
}
